import { deliveryWorkflowTypes, workflowTypes } from "./jobs";

/**
 * One entry per workflow provider. Workflow keys are `<provider>.<name>`, so
 * the prefix is the only routing key the system needs. Collection providers
 * own sources and may be scheduled; delivery providers own connections and
 * only ever run when someone sends. Adding a provider is a single entry here.
 */
export const workflowProviders = {
  github: { kind: "collection", producerName: "GitHub collector" },
  reddit: { kind: "collection", producerName: "Reddit collector" },
  slack: { kind: "delivery" },
  webhook: { kind: "delivery" },
} as const;

export type WorkflowProvider = keyof typeof workflowProviders;
export type WorkflowKind = (typeof workflowProviders)[WorkflowProvider]["kind"];

export const collectionProviders = {
  github: workflowProviders.github,
  reddit: workflowProviders.reddit,
} as const;

export type CollectionProvider = keyof typeof collectionProviders;

export const collectionProviderNames = Object.keys(collectionProviders) as CollectionProvider[];

export function isWorkflowProvider(value: string): value is WorkflowProvider {
  return value in workflowProviders;
}

export function isCollectionProvider(value: string): value is CollectionProvider {
  return value in collectionProviders;
}

/** The provider that owns a workflow key: `github.sync_issues` → `github`. */
export function workflowProvider(workflowKey: string) {
  const [provider] = workflowKey.split(".");
  return provider && isWorkflowProvider(provider) ? provider : null;
}

/** True when a workflow key belongs to a provider. */
export function workflowBelongsTo(workflowKey: string, provider: string) {
  return workflowProvider(workflowKey) === provider;
}

/** Whether a workflow key collects from a source or delivers to a connection. */
export function workflowKind(workflowKey: string): WorkflowKind | null {
  const provider = workflowProvider(workflowKey);
  return provider ? workflowProviders[provider].kind : null;
}

export function isDeliveryWorkflow(
  workflowKey: string,
): workflowKey is keyof typeof deliveryWorkflowTypes {
  return workflowKey in deliveryWorkflowTypes;
}

/**
 * The named Temporal workflow a trigger or schedule starts for a catalog key.
 * Schedules and manual triggers start the same type, so a schedule's action is
 * reconciled against it.
 */
export function workflowTypeFor(workflowKey: keyof typeof workflowTypes) {
  return workflowTypes[workflowKey];
}
export const collectionWorkflowFor = workflowTypeFor;
